/**
 * OptimizedImage Component
 *
 * Uses Cloudflare Images API with pre-defined variants for optimized delivery.
 */

import { type ImgHTMLAttributes } from 'react';

type ImageVariant = 'thumbnail' | 'medium' | 'large' | 'hero' | 'public';

interface OptimizedImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'srcSet'> {
  src: string;
  variant?: ImageVariant;
  width?: number;
  height?: number;
}

export function OptimizedImage({
  src,
  alt,
  variant = 'medium',
  width,
  height,
  className,
  loading = 'lazy',
  ...props
}: OptimizedImageProps) {
  const IMAGE_CDN_BASE = 'https://images.smallbizinnovation.com';
  const cleanSrc = src.replace(/^(\/|m\/)/, '');
  const imageSrc = `${IMAGE_CDN_BASE}/m/${cleanSrc}/${variant}`;

  return (
    <img
      src={imageSrc}
      alt={alt}
      width={width}
      height={height}
      className={className}
      loading={loading}
      {...props}
    />
  );
}

export function TeamPhoto({
  filename,
  name,
  className = 'w-full h-full object-cover',
}: {
  filename: string;
  name: string;
  className?: string;
}) {
  return (
    <OptimizedImage
      src={filename}
      variant="thumbnail"
      width={168}
      height={170}
      alt={`${name}, SmallBiz Innovation team member`}
      className={className}
    />
  );
}

export function SBILogo({ size = 40, className }: { size?: number; className?: string }) {
  return (
    <div className="relative inline-flex items-center justify-center">
      <OptimizedImage
        src="logos/logo-profile.png"
        variant="thumbnail"
        width={size}
        height={size}
        alt="SmallBiz Innovation logo"
        className={
          className ||
          'rounded-full object-cover shadow-lg ring-2 ring-primary/20 transition-all duration-300 hover:ring-primary/40 hover:shadow-xl hover:scale-105'
        }
        loading="eager"
      />
    </div>
  );
}
